import {
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  type SelectChangeEvent,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

export interface FieldOption {
  value: string;
  label: string;
}

/**
 * Field-scoped search for super-admin pages (Registered Users, Subscription,
 * Vendors): a dropdown to pick which field to search by, a text input, and a
 * search icon button that triggers the search explicitly — no live debounce,
 * the caller decides what "search" means (click, or Enter).
 *
 * Unlike SearchWithFieldSelect (used by the tenant/party screens), the field
 * option set is passed in by the caller since it differs per admin page
 * (Tenants/Subscription: Name/GST/Phone/Sl Number; Vendors: Name/City/GST/Phone).
 */
export function AdminSearchWithFieldSelect({
  field, onFieldChange, options, value, onChangeText, onSearch, placeholder, sx,
}: {
  field: string;
  onFieldChange: (field: string) => void;
  options: FieldOption[];
  value: string;
  onChangeText: (text: string) => void;
  onSearch: () => void;
  placeholder?: string;
  sx?: object;
}) {
  const fieldLabel = options.find(o => o.value === field)?.label ?? options[0]?.label ?? '';
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={sx}>
      <Select
        size="small"
        value={field}
        onChange={(e: SelectChangeEvent) => onFieldChange(e.target.value)}
        sx={{ minWidth: 130 }}
      >
        {options.map(opt => (
          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
        ))}
      </Select>
      <TextField
        size="small"
        placeholder={placeholder ?? `Search by ${fieldLabel.toLowerCase()}…`}
        value={value}
        onChange={e => onChangeText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') onSearch(); }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={onSearch} aria-label="search" edge="end">
                <SearchIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ minWidth: { xs: 200, sm: 260 } }}
      />
    </Stack>
  );
}
